import { WSList } from "@wingspanhq/fe-component-library";

import { AIValidationWidget } from "../../../../components/AIValidationWidget";
import {
  RequirementContextType,
  RequirementTableCallback
} from "../../../../types";
import { WayfinderWidget } from "../../../../../../shared/components/WayfinderWidget";
import {
  IDENTITY_VERIFICATION_CHECKS,
  IDENTITY_VERIFICATION_FAILURE_RISK_SIGNALS
} from "../../../../../RequirementDefinitions/modules/IdentityVerification/createRequirementDefinition/CreateIdentityVerificationPartialForm";

import { IdentityVerificationRequestStatus } from "@wingspanhq/users/dist/lib/interfaces/identityVerificationRequest";
import { IIdentityVerificationRequestResponse } from "@wingspanhq/users/dist/lib/interfaces/api/identityVerificationRequest";
import { SharedFileRequestJobStatus } from "@wingspanhq/shared-files/dist/lib/interfaces";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { ValidationItemProps } from "../../../../components/AIValidationWidget/ValidationItem";

interface Props {
  identityVerificationRequest: IIdentityVerificationRequestResponse;
  contextType: RequirementContextType;
  requirement: IRequirementResponse;
  onSuccess: RequirementTableCallback;
  isInitialLoading: boolean;
}

export const IdentityVerificationDetailsWidgetsContainer: React.FC<Props> = ({
  identityVerificationRequest,
  contextType,
  isInitialLoading
}) => {
  const riskSignals =
    identityVerificationRequest?.identityVerification?.riskSignals;

  // go over all possible checks
  const validationItems: ValidationItemProps[] =
    IDENTITY_VERIFICATION_CHECKS.map(checkFixture => {
      // find signals from this check in incoming signals
      const aggregatedErrors = Object.keys(checkFixture.riskSignals).reduce(
        (errors, signal) => {
          const isErrorRiskPresent = riskSignals?.find(
            incomingSignal =>
              signal === incomingSignal &&
              IDENTITY_VERIFICATION_FAILURE_RISK_SIGNALS.includes(
                incomingSignal
              )
          );
          if (isErrorRiskPresent) {
            if (errors.length) {
              errors += "\n\n";
            }
            errors += checkFixture.riskSignals[signal];
          }
          return errors;
        },
        ""
      );

      // find signals from this check in incoming signals
      const aggregatedWarnings = Object.keys(checkFixture.riskSignals).reduce(
        (warnings, signal) => {
          const isWarningRiskPresent = riskSignals?.find(
            incomingSignal =>
              signal === incomingSignal &&
              !IDENTITY_VERIFICATION_FAILURE_RISK_SIGNALS.includes(
                incomingSignal
              )
          );
          if (isWarningRiskPresent) {
            if (warnings.length) {
              warnings += "\n\n";
            }
            warnings += checkFixture.riskSignals[signal];
          }
          return warnings;
        },
        ""
      );

      const hasErrors = aggregatedErrors.length > 0;
      const hasWarnings = aggregatedWarnings.length > 0;

      let status: SharedFileRequestJobStatus;

      if (
        identityVerificationRequest?.status ===
        IdentityVerificationRequestStatus.Complete
      ) {
        status = SharedFileRequestJobStatus.Complete;
      } else if (
        identityVerificationRequest?.status ===
        IdentityVerificationRequestStatus.Failed
      ) {
        if (hasErrors) {
          status = SharedFileRequestJobStatus.Failed;
        } else {
          status = SharedFileRequestJobStatus.Complete;
        }
      } else if (
        identityVerificationRequest?.status ===
          IdentityVerificationRequestStatus.Pending &&
        isInitialLoading
      ) {
        status = SharedFileRequestJobStatus.Processing;
      } else {
        status = SharedFileRequestJobStatus.Pending;
      }

      return {
        title: checkFixture.title,
        description: checkFixture.description,
        type: checkFixture.type,
        status,
        statusReason:
          status === SharedFileRequestJobStatus.Failed
            ? aggregatedErrors
            : undefined,
        statusSummary:
          status === SharedFileRequestJobStatus.Failed
            ? checkFixture.errorSummary
            : status === SharedFileRequestJobStatus.Complete
            ? hasWarnings
              ? checkFixture.successWithWarningsSummary
              : checkFixture.successSummary
            : undefined,
        warningReason: aggregatedWarnings
      };
    });

  return (
    <WSList gap="L">
      <AIValidationWidget
        contextType={contextType}
        validationItems={validationItems}
      />
      {/* Show WayFinder widget only for payers */}
      {contextType === RequirementContextType.Payee && <WayfinderWidget />}
    </WSList>
  );
};
